import React, { useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { BagelAiFlowEdge, BagelAiFlowNode } from '../../types/aiFlow';
import {
  buildHighlightedEdgeKeys,
  computeAiFlowRankLayout,
  layoutToPixelPosition,
} from '../../utils/aiFlowRankLayout';
import FlowNode, { type FlowNodeData } from './FlowNode';
import styles from './AiFlowTab.module.scss';

const nodeTypes = { flowNode: FlowNode };

type Props = {
  nodes: BagelAiFlowNode[];
  edges: BagelAiFlowEdge[];
  highlightedPath: string[];
  onNodeClick: (node: BagelAiFlowNode) => void;
};

export default function AiFlowCanvas({
  nodes,
  edges,
  highlightedPath,
  onNodeClick,
}: Props) {
  const highlightedNodes = useMemo(
    () => new Set(highlightedPath),
    [highlightedPath],
  );
  const highlightedEdges = useMemo(
    () => buildHighlightedEdgeKeys(highlightedPath),
    [highlightedPath],
  );

  const layout = useMemo(
    () => computeAiFlowRankLayout(
      nodes.map(n => n.id),
      edges,
    ),
    [nodes, edges],
  );

  const flowNodes: Node[] = useMemo(
    () =>
      nodes.map(node => {
        const pos = layoutToPixelPosition(
          layout.get(node.id) ?? { column: 0, row: 0 },
        );
        const data: FlowNodeData = {
          kind: node.kind,
          title: node.title,
          description: node.description,
          editable: node.editable,
          highlighted: highlightedNodes.has(node.id),
          callsLlm: node.callsLlm,
          tools: node.tools?.map(t => t.name),
        };
        return {
          id: node.id,
          type: 'flowNode',
          position: pos,
          data,
          draggable: false,
          selectable: true,
        };
      }),
    [nodes, layout, highlightedNodes],
  );

  const flowEdges: Edge[] = useMemo(
    () =>
      edges.map(edge => {
        const key = `${edge.from}->${edge.to}`;
        const active = highlightedEdges.has(key);
        return {
          id: key,
          source: edge.from,
          target: edge.to,
          label: edge.label,
          type: 'smoothstep',
          animated: active,
          style: {
            stroke: active ? '#2563eb' : '#94a3b8',
            strokeWidth: active ? 2.5 : 1.5,
            strokeDasharray: '6 4',
          },
          labelStyle: { fill: '#475569', fontSize: 11 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: active ? '#2563eb' : '#94a3b8',
          },
        };
      }),
    [edges, highlightedEdges],
  );

  const nodeById = useMemo(
    () => new Map(nodes.map(n => [n.id, n])),
    [nodes],
  );

  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, rfNode: Node) => {
      const node = nodeById.get(rfNode.id);
      if (!node) return;

      const instance = reactFlowInstanceRef.current;
      if (instance) {
        const width = rfNode.measured?.width ?? 120;
        const height = rfNode.measured?.height ?? 60;
        const centerX = rfNode.position.x + width / 2;
        const centerY = rfNode.position.y + height / 2;
        instance.setCenter(centerX, centerY, { zoom: 1.5, duration: 500 });
      }

      onNodeClick(node);
    },
    [nodeById, onNodeClick],
  );

  return (
    <div className={styles.canvasWrap}>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesConnectable={false}
        nodesDraggable={false}
        elementsSelectable={false}
        onInit={instance => {
          reactFlowInstanceRef.current = instance;
        }}
        onNodeClick={handleNodeClick}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} color="#e2e8f0" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
